import React from "react";
import RatingBadge from "../reviews/components/RatingBadge";
import { REVIEWS } from "../constants/testIds/reviews";
import {
  countryText, headlineText, languagesText, lastSessionText, memberSinceText,
} from "./profileFacts";

const CONTACT_CLASS =
  "inline-flex items-center justify-center mt-5 px-5 h-10 rounded-lg border border-white/40 text-sm font-medium transition";

/**
 * "Get to know {name}" — Fiverr's seller block, for a practice.
 *
 * THE SHAPE, top to bottom, as Fiverr lays it out: a round photo beside the name, the
 * one-line headline and the rating; the specialty chips; ONE outlined "Contact me"; then a
 * bordered panel holding a two-column grid of short facts (from · member since · last
 * session · languages), a rule, and the practitioner's own description.
 *
 * NOTHING IS INVENTED. Each row comes from the profile payload, and a row whose value is
 * empty is not rendered — no "—", no "not specified", no sample text. A practice that has
 * written nothing still gets its name, its rating and whatever the platform itself knows
 * (when it joined, when it last held a session). The panel disappears entirely when it
 * would hold nothing at all.
 *
 * PRESENTATIONAL. The caller passes the payload, `t` (the "marketplace" namespace) and the
 * language, so the same component renders the public page AND the live preview in the
 * practitioner's profile editor — the practitioner sees exactly what a visitor will see.
 * `preview` is the editor's case: the title drops to an `<h2>` (the editor page owns its
 * `<h1>`), the rating is left out (a preview has no business re-reading reviews), and
 * "Contact me" is drawn but is not a link — the editor cannot know where the practice's
 * site will send it, and a button that goes nowhere must not pretend otherwise.
 *
 * @param {object} profile  the `/marketplace/practitioners/{slug}` payload, or the editor's
 *                          form merged over the practice's name and logo
 * @param {function} t      `useT("marketplace")`
 * @param {string} lang     the visitor's language, for every `Intl` formatter
 * @param {boolean} [preview]
 * @param {number} [now]    the clock, for "last session" (tests pin it)
 */
export default function PractitionerAbout({ profile, t, lang, preview = false, now }) {
  const Title = preview ? "h2" : "h1";
  const name = profile.name || "";
  const headline = headlineText(profile, lang);
  const specialties = profile.specialties || [];
  const photo = profile.photo_url || profile.logo_url || "";

  const facts = [
    { key: "from", label: t("about.from"), value: countryText(profile.country, lang) },
    { key: "memberSince", label: t("about.memberSince"), value: memberSinceText(profile.member_since, lang) },
    { key: "lastSession", label: t("about.lastSession"), value: lastSessionText(profile.last_session_at, lang, now) },
    { key: "languages", label: t("about.languages"), value: languagesText(profile.languages, lang) },
  ].filter((f) => f.value);
  const bio = (profile.bio || "").trim();

  return (
    <section data-testid={REVIEWS.profileAbout} aria-labelledby="practitioner-about-title">
      <Title id="practitioner-about-title" className="font-display text-2xl md:text-3xl">
        {t("about.title", { name })}
      </Title>

      <div className="flex items-center gap-5 mt-6">
        {photo ? (
          <img
            src={photo}
            alt=""
            data-testid={REVIEWS.profilePhoto}
            className="w-24 h-24 rounded-full object-cover flex-shrink-0 border border-white/10"
          />
        ) : (
          // The initial is drawn in the platform's gold, NOT in the practice's primary
          // colour: a primary colour is chosen for the practice's own light site and is
          // routinely near-black, which made the initial invisible on this dark page.
          <div
            aria-hidden="true"
            className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-display flex-shrink-0 bg-sol-gold/15 text-sol-gold"
          >
            {(name || "?").charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0">
          <p className="font-display text-xl">{name}</p>
          {headline && (
            <p className="text-sm text-white/70 mt-1" data-testid={REVIEWS.profileHeadline}>{headline}</p>
          )}
          {!preview && (
            <RatingBadge rating={profile.rating_summary} tenantId={profile.tenant_id} t={t} variant="dark" />
          )}
        </div>
      </div>

      {specialties.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-4">
          {specialties.map((s) => (
            <span key={s} className="text-[11px] bg-sol-gold/15 text-sol-gold px-2.5 py-0.5 rounded-full capitalize">
              {s}
            </span>
          ))}
        </div>
      )}

      {preview ? (
        <span aria-hidden="true" className={`${CONTACT_CLASS} opacity-80`}>{t("about.contact")}</span>
      ) : profile.contact_url && (
        <a href={profile.contact_url} data-testid={REVIEWS.profileContact} className={`${CONTACT_CLASS} hover:bg-white/5`}>
          {t("about.contact")}
        </a>
      )}

      {(facts.length > 0 || bio) && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid={REVIEWS.profileFacts}>
          {facts.length > 0 && (
            <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-4 m-0">
              {facts.map((f) => (
                <div key={f.key} data-testid={REVIEWS.profileFact(f.key)}>
                  <dt className="text-sm text-white/50">{f.label}</dt>
                  <dd className="m-0 mt-0.5 font-medium text-white/90">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {facts.length > 0 && bio && <hr className="my-5 border-white/10" />}
          {bio && (
            <p className="text-sm leading-relaxed text-white/75 whitespace-pre-line break-words m-0" data-testid={REVIEWS.profileBio}>
              {bio}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
